import React, { useState } from 'react';
import { submitFeedback } from '../../services/feedbackApi';
import { isThrottled, setLastSubmit } from '../../utils/feedbackThrottle';

// issue #162: backend (score-engine feedback blueprint) と一致させる文字数上限 (trim 後)。
export const TITLE_MAX_LEN = 100;
export const BODY_MAX_LEN = 2000;

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'throttled';

// status ごとの通知メッセージと色。idle / submitting は通知を出さない (未収載)。
const STATUS_NOTICE: Partial<Record<Status, { text: string; className: string }>> = {
  success: { text: '送信しました。ありがとうございます。', className: 'text-green-600' },
  error: { text: '送信に失敗しました。しばらくして再度お試しください', className: 'text-red-600' },
  throttled: {
    text: '短時間に何度も送信できません。しばらく時間をおいてからお試しください。',
    className: 'text-amber-600',
  },
};

/**
 * ユーザ要望フォーム。タイトル + 本文の2項目のみ。trim 後の文字数で送信可否を判定し、
 * 連投防止 (isThrottled) と送信成功/失敗を文言で伝える。issue URL / GitHub の存在は一切見せない。
 */
const FeedbackForm: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const isValid =
    trimmedTitle.length >= 1 &&
    trimmedTitle.length <= TITLE_MAX_LEN &&
    trimmedBody.length >= 1 &&
    trimmedBody.length <= BODY_MAX_LEN;
  const submitting = status === 'submitting';

  // 入力が変わったら表示中の通知 (success/error/throttled) を消す。
  const resetNotice = (): void => {
    if (status !== 'idle') setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!isValid || submitting) return;
    // 連投防止: クールダウン中は送信せず注意文言のみ (backend レート制限の肩代わり)。
    if (isThrottled()) {
      setStatus('throttled');
      return;
    }
    setStatus('submitting');
    try {
      await submitFeedback({ title: trimmedTitle, body: trimmedBody });
      setLastSubmit();
      setTitle('');
      setBody('');
      setStatus('success');
    } catch {
      // GitHub の事情はユーザに見せず汎用の失敗文言のみ。入力は保持して再送しやすくする。
      setStatus('error');
    }
  };

  const notice = STATUS_NOTICE[status];

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-7"
    >
      <div className="mb-5">
        <label htmlFor="feedback-title" className="block text-sm font-bold text-slate-700 mb-1.5">
          タイトル <span className="text-red-500">*</span>
        </label>
        <input
          id="feedback-title"
          type="text"
          value={title}
          maxLength={TITLE_MAX_LEN}
          onChange={(e) => {
            setTitle(e.target.value);
            resetNotice();
          }}
          placeholder="例: 音源の音量をもう少し上げたい"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-slate-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
        />
        <div className="mt-1 text-right text-xs text-gray-400">
          {trimmedTitle.length} / {TITLE_MAX_LEN}
        </div>
      </div>

      <div className="mb-5">
        <label htmlFor="feedback-body" className="block text-sm font-bold text-slate-700 mb-1.5">
          ご要望・お困りごとの内容 <span className="text-red-500">*</span>
        </label>
        <textarea
          id="feedback-body"
          value={body}
          rows={6}
          maxLength={BODY_MAX_LEN}
          onChange={(e) => {
            setBody(e.target.value);
            resetNotice();
          }}
          placeholder="どんな場面で、どうなってほしいかを教えてください。"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-slate-800 placeholder:text-gray-400 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-y"
        />
        <div className="mt-1 text-right text-xs text-gray-400">
          {trimmedBody.length} / {BODY_MAX_LEN}
        </div>
      </div>

      {notice && (
        <p
          role={status === 'error' ? 'alert' : 'status'}
          className={`mb-4 text-sm font-medium ${notice.className}`}
        >
          {notice.text}
        </p>
      )}

      <button
        type="submit"
        disabled={!isValid || submitting}
        className="w-full sm:w-auto px-8 py-2.5 rounded-xl bg-primary-500 text-white font-bold shadow-sm hover:bg-primary-600 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-500 transition"
      >
        {submitting ? '送信中…' : '送信する'}
      </button>
    </form>
  );
};

export default FeedbackForm;
